/**
 * Vehicle Investigation Adapter
 *
 * Fetches the pre-built vehicle day case or window projection from the backend:
 *   GET /projection/vehicle/:vehicleId/day-case/:date       (legacy)
 *   GET /projection/vehicle/:vehicleId/window?from=&to=     (new)
 *
 * All milestone resolution, GPS sampling, and evidence gathering
 * is handled server-side by the builders.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import type { VehicleDayCase, VehicleWindowProjection } from '../types/vehicleInvestigation';

/**
 * Fetch the vehicle day case for a given vehicle and date.
 * @deprecated Use fetchVehicleWindowProjection for new code.
 */
export async function fetchVehicleDayCase(
  vehicleId: string,
  date: string,
): Promise<VehicleDayCase> {
  const url = API_ENDPOINTS.vehicleProjection.dayCase(vehicleId, date);
  const res = await authenticatedFetch(url);

  if (res.status === 404) {
    throw new Error('Vehicle not found or no data available for this date');
  }
  if (!res.ok) {
    throw new Error(`Vehicle day case fetch failed: ${res.status}`);
  }

  return res.json();
}

/**
 * Fetch the vehicle window projection for an arbitrary time window.
 */
export async function fetchVehicleWindowProjection(
  vehicleId: string,
  from: string,
  to: string,
): Promise<VehicleWindowProjection> {
  const url = API_ENDPOINTS.vehicleProjection.windowProjection(vehicleId, from, to);
  const res = await authenticatedFetch(url);

  if (res.status === 404) {
    throw new Error('Vehicle not found or no data available for this window');
  }
  if (!res.ok) {
    throw new Error(`Vehicle window projection fetch failed: ${res.status}`);
  }

  return res.json();
}

/**
 * Fetch the vehicle window projection for a single operational date.
 * Convenience wrapper that auto-expands to the default 04:00-04:00 window.
 */
export async function fetchVehicleWindowProjectionByDate(
  vehicleId: string,
  date: string,
): Promise<VehicleWindowProjection> {
  const url = API_ENDPOINTS.vehicleProjection.windowProjectionByDate(vehicleId, date);
  const res = await authenticatedFetch(url);

  if (res.status === 404) {
    throw new Error('Vehicle not found or no data available for this date');
  }
  if (!res.ok) {
    throw new Error(`Vehicle window projection fetch failed: ${res.status}`);
  }

  return res.json();
}
